import { useState, useEffect, useCallback } from 'react';

interface ServiceWorkerState {
  isSupported: boolean;
  isRegistered: boolean;
  updateAvailable: boolean;
  applyUpdate: () => void;
  currentVersion: string | null;
}

export const useServiceWorker = (): ServiceWorkerState => {
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);

  const isSupported = 'serviceWorker' in navigator;

  useEffect(() => {
    if (!isSupported) {
      return;
    }

    // Development: unregister any existing SW to prevent stale caches
    if (process.env.NODE_ENV === 'development') {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((reg) => reg.unregister());
      });
      caches.keys().then((names) => {
        names.forEach((name) => caches.delete(name));
      });
      return;
    }

    registerServiceWorker();
    getCurrentVersion();

    const handleControllerChange = () => {
      window.location.reload();
    };

    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
    };
  }, [isSupported]);

  const registerServiceWorker = async () => {
    try {
      const reg = await navigator.serviceWorker.register('/sw.js');
      setRegistration(reg);

      reg.addEventListener('updatefound', () => {
        const newWorker = reg.installing;
        if (!newWorker) return;

        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            setUpdateAvailable(true);
          }
        });
      });

      if (reg.waiting) {
        setUpdateAvailable(true);
      }

      reg.update();

      setInterval(() => {
        reg.update();
      }, 10 * 60 * 1000);
    } catch (error) {
      console.error('Service Worker registration failed:', error);
    }
  };

  const getCurrentVersion = async () => {
    try {
      const cacheNames = await caches.keys();
      const appCache = cacheNames.find((name) => name.startsWith('app-cache-v'));
      if (appCache) {
        const version = appCache.replace('app-cache-v', '');
        setCurrentVersion(version);
      }
    } catch (error) {
      console.error('Error getting cache version:', error);
    }
  };

  const applyUpdate = useCallback(() => {
    if (!registration || !registration.waiting) {
      return;
    }

    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
  }, [registration]);

  return {
    isSupported,
    isRegistered: !!registration,
    updateAvailable,
    applyUpdate,
    currentVersion,
  };
};
